/**
 * Toasts (v7 Part D §10.3): a short message at the foot of the screen, gone after a while; optionally with Undo.
 *
 *   toast('This page moved to Operations › Routes & riders.')
 *   toast('Skipped for 03 Oct.', { undo: () => rpc(...) })   // Undo for 10 seconds
 *
 * One host (<ToastHost/>) is mounted in the shell. A message can be handed across a redirect: `toastAfterNavigation`
 * keeps it until the next screen mounts.
 */
import { useEffect, useState } from 'react';
import { cx } from './primitives';

interface Toast { id: number; text: string; undo?: () => unknown; tone: 'info' | 'ok' | 'warn' | 'bad' }
type Listener = (t: Toast[]) => void;
let items: Toast[] = [];
let seq = 0;
const listeners = new Set<Listener>();
const emit = () => listeners.forEach((l) => l(items));

export function toast(text: string, opts: { undo?: () => unknown; tone?: Toast['tone']; ms?: number } = {}) {
  const t: Toast = { id: ++seq, text, undo: opts.undo, tone: opts.tone ?? 'info' };
  items = [...items, t].slice(-3);
  emit();
  setTimeout(() => { items = items.filter((x) => x.id !== t.id); emit(); }, opts.ms ?? (opts.undo ? 10_000 : 6_000));
}

const PENDING = 'rtcom.toast-after-nav';
/** Shows the message on the next screen (used by redirects). */
export function toastAfterNavigation(text: string) {
  try { sessionStorage.setItem(PENDING, text); } catch { toast(text); }
}

export function ToastHost() {
  const [list, setList] = useState<Toast[]>(items);
  useEffect(() => {
    listeners.add(setList);
    try { const p = sessionStorage.getItem(PENDING); if (p) { sessionStorage.removeItem(PENDING); toast(p); } } catch { /* storage blocked */ }
    return () => { listeners.delete(setList); };
  }, []);
  if (!list.length) return null;
  return (
    <div role="status" aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex flex-col items-center gap-2 px-4">
      {list.map((t) => (
        <div key={t.id} className={cx('pointer-events-auto flex max-w-xl items-center gap-3 rounded-[var(--radius-control)] px-4 py-2 text-body shadow-[var(--shadow-overlay)]',
          t.tone === 'bad' ? 'bg-refused text-white' : t.tone === 'ok' ? 'bg-accepted text-white' : t.tone === 'warn' ? 'bg-queued text-white' : 'bg-ink text-surface')}>
          <span>{t.text}</span>
          {t.undo ? (
            <button type="button" className="font-semibold underline" onClick={() => { void t.undo!(); items = items.filter((x) => x.id !== t.id); emit(); }}>Undo</button>
          ) : null}
        </div>))}
    </div>
  );
}
